import { FC, useMemo } from 'react';
import { ExaminationPageSectionId } from '../models';
import { FormattedMessage, useIntl } from 'react-intl';
import { AuscultationGroup } from '@higo/common/src/components';
import styled from 'styled-components';
import { ExaminationPageSection } from './ExaminationPageSection';
import {
  useAbdominalAuscultationPhysicalExaminationQuery,
  useAbdominalAuscultationPhysicalExaminationResourceFiles,
} from '@higo/common/src/features/ExaminationPage/hooks/queries';
import { buildAuscultationPoints } from '@higo/common/src/features/ExaminationPage/utils/buildAuscultationPoints';
import { auscultationPointsCount } from '@higo/common/src/config/auscultationPointsCount';
import { PhysicalExaminationType } from '@higo/api';
import { media } from '@higo/ui';

const AuscultationGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  ${media.mobile.max} {
    gap: 0.5rem;
  }
`;

export interface AbdominalAuscultationSectionProps {
  examinationId: number;
  isStatusCanceledByUser?: boolean;
  isVisitSection?: boolean;
}

export const AbdominalAuscultationSection: FC<AbdominalAuscultationSectionProps> =
  ({ examinationId, isStatusCanceledByUser, isVisitSection }) => {
    const intl = useIntl();
    const { data } =
      useAbdominalAuscultationPhysicalExaminationQuery(examinationId);

    const { data: fileResourceMap } =
      useAbdominalAuscultationPhysicalExaminationResourceFiles(examinationId);

    const auscultationPoints = useMemo(
      () =>
        buildAuscultationPoints(
          data?.abdominalAuscultationResults ?? [],
          fileResourceMap ?? {},
          auscultationPointsCount[
            PhysicalExaminationType.AbdominalAuscultation
          ],
          (point) =>
            `${intl.formatMessage({
              id: 'common.point',
            })} ${point}`,
        ),
      [data?.abdominalAuscultationResults, fileResourceMap, intl],
    );

    const filesIds = useMemo(
      () => auscultationPoints.filter((x) => x.file).map((x) => x.file?.fileId),
      [auscultationPoints],
    );

    return (
      // eslint-disable-next-line react/jsx-no-undef
      <ExaminationPageSection
        title={
          <FormattedMessage id="examinationPage.section.abdominalAuscultation.title" />
        }
        sectionId={ExaminationPageSectionId.Abdominal}
        audioDownload="double"
        filesIds={filesIds}
        isStatusCanceledByUser={isStatusCanceledByUser}
        isVisitSection={isVisitSection}
      >
        <AuscultationGroupContainer>
          <AuscultationGroup auscultationPoints={auscultationPoints} />
        </AuscultationGroupContainer>
      </ExaminationPageSection>
    );
  };
